import type {
    TextRenderGraphicListMarker,
    TextRenderHighlight,
    TextRenderInput,
    TextRenderListMarker,
    TextRenderSpan,
    TextRenderTexture,
} from '@/framework/services/text-render';
import { isSafari } from '@/utils/browser-kit';
import { colorToRgbaUint32 } from '@/utils/color-kit';
import { convertImageToWebp, imageConverter } from '@/utils/image-resource-kit';
import {
    TextWritingType,
    type ILayerProps,
    type IListStyleMaterial,
    type ITextContent,
    type ITextLayerProps,
    type ITextMark,
    type ITexture,
    type Optional,
} from '@/version-types';
import type { HighlightMaterial } from '../../../framework/services/text-highlight';
import { shouldKeepLegacyFrameUnwrapped } from './legacy-frame';

type HighlightResolver = (id: string) => HighlightMaterial | undefined;

/**
 * 去除素材 URL 首尾空白，作为 Renderer 缓存的稳定输入。
 *
 * @param url - 待规范化的素材 URL。
 * @returns 去除首尾空白后的 URL。
 */
const normalizeAssetURL = (url: string): string => url.trim();

/**
 * 沿用 Text Layer 现有浏览器兼容策略规范化纹理 URL。
 *
 * @param url - 持久化纹理 URL。
 * @returns 可交给 Text Renderer 加载的图片 URL。
 */
const normalizeTextureURL = (url: string): string => {
    const normalized = normalizeAssetURL(url);
    return isSafari ? imageConverter(normalized, 150, 100) : convertImageToWebp(normalized);
};

/**
 * 把 Text Layer 纹理配置转换为 Renderer 输入。
 *
 * @param texture - 当前图层纹理配置。
 * @returns Renderer 可加载并同步绘制的纹理。
 */
const createTextureAsset = (texture: ITexture): TextRenderTexture => ({
    url: normalizeTextureURL(texture.url),
    stretch: texture.stretch,
});

/**
 * 把自定义列表素材转换为 Renderer 的图形型 marker 声明。
 *
 * Renderer 只区分位图与 SVG 两种图形形态，宿主侧的 `custom` 样式在此完成翻译；编号、颜色槽等能力
 * 只有 SVG 素材支持，因此不写入位图 marker。
 *
 * @param material - 当前图层自定义 marker 素材。
 * @param numberFontFamily - SVG 自动编号已经由宿主解析的 runtime 字体族。
 * @returns Renderer 可加载并同步绘制的图形型 marker。
 */
const createGraphicListMarker = (
    material: IListStyleMaterial,
    numberFontFamily?: string,
): TextRenderGraphicListMarker => {
    const size = {
        url: normalizeAssetURL(material.url),
        width: material.iconWidth,
        height: material.iconHeight,
        baseContentTextSize: material.baseContentTextSize,
        scale: material.scale,
    };

    if (!material.url.trim().toLowerCase().endsWith('.svg')) return { kind: 'image', ...size };

    return {
        kind: 'svg',
        ...size,
        autoIncrement: material.autoIncrement,
        padZero: material.padZero,
        fontColor: material.fontColor,
        iconColors: material.iconColors,
        numberFontSize: material.fontSize,
        numberFontFamily,
    };
};

/**
 * 把持久化 Highlight 使用参数复制为宿主无关的 Renderer 输入。
 *
 * @param highlight - 图层级或分段级 Highlight 引用。
 * @param highlightResolver - 按素材 ID 读取当前 Highlight 配置。
 * @returns Text Renderer 可消费的 Highlight；未声明时保持原空值，素材缺失时返回 null。
 */
const createHighlight = (
    highlight: Optional<ITextMark>,
    highlightResolver: HighlightResolver,
): Optional<TextRenderHighlight> => {
    if (!highlight) return highlight;

    const material = highlightResolver(highlight.materialId);
    if (!material?.data.src) return null;

    return {
        id: highlight.materialId,
        color: highlight.color,
        size: highlight.size,
        config: material.data,
    };
};

/**
 * 把 Text Layer 富文本分段转换为宿主无关的渲染分段。
 *
 * @param item - 当前持久化富文本分段。
 * @param highlightResolver - 按素材 ID 读取当前 Highlight 配置。
 * @param enableHighlight - 当前宿主能力是否允许 Highlight。
 * @returns Text Renderer 可消费的完整分段样式。
 */
const createSpan = (
    item: ITextContent,
    highlightResolver: HighlightResolver,
    enableHighlight = true,
): TextRenderSpan => ({
    text: item.text,
    fontFamily: item.fontFamily,
    fontSize: item.fontSize,
    colorRgba: colorToRgbaUint32(item.color),
    bold: item.bold,
    italic: item.italic,
    lineThrough: item.lineThrough,
    underline: item.underline,
    highlight: enableHighlight ? createHighlight(item.textMark, highlightResolver) : undefined,
    rotate: item.rotate,
});

/**
 * 根据 Text Layer 数据创建列表 marker 声明。
 *
 * 宿主的 `custom` 样式在此翻译为图形型 marker；Renderer 不感知样式来源，只接收字符型或图形型形态。
 *
 * @param data - 当前 Text Layer 数据。
 * @param graphic - 已归一化的可选图形型 marker 声明。
 * @returns Renderer 可消费的列表 marker；未启用或图形素材缺失时返回 undefined。
 */
const createListMarker = (
    data: ITextLayerProps,
    graphic?: TextRenderGraphicListMarker,
): TextRenderListMarker | undefined => {
    if (!data.listStyle) return undefined;
    if (data.listStyle !== 'custom') return { kind: 'character', style: data.listStyle };
    return graphic;
};

/**
 * 将真实 Text Layer 数据归一化为独立于宿主的 Text Render Input。
 *
 * 该边界只做字段、能力与素材声明映射，不启动字体或素材加载，也不携带图层身份、生命周期或编辑状态。
 *
 * @param layer - 当前真实 Text Layer 或编辑期间的候选图层数据。
 * @param highlightResolver - 按素材 ID 读取当前 Highlight 配置。
 * @returns 单个 Text Renderer layout/render 调用所需的完整输入。
 */
export const createTextRenderInputFromTextLayer = (
    layer: ILayerProps,
    highlightResolver: HighlightResolver,
): TextRenderInput => {
    const data = layer.data as ITextLayerProps;
    const fixedInlineSize = layer.__fixedWidth !== false;
    const isHorizontal = data.writingMode === TextWritingType.HORIZONTAL_TB;

    const enableList = !data.shapeExtension;
    const enableHighlight = !data.shapeExtension && isHorizontal;
    const enableTexture = !data.shapeExtension;
    const enableArtText = !data.shapeExtension && isHorizontal;

    const highlight = enableHighlight ? createHighlight(data.textMark, highlightResolver) : undefined;
    const graphicListMarker =
        enableList && data.listStyle === 'custom' && data.listStyleMaterial?.url
            ? createGraphicListMarker(data.listStyleMaterial, layer.__listMarkerFontFamily)
            : undefined;
    const listMarker = createListMarker(data, graphicListMarker);

    const input: TextRenderInput = {
        text: data.text,
        fontFamily: data.fontFamily,
        fontSize: data.fontSize,
        colorRgba: colorToRgbaUint32(data.color),
        bold: data.bold,
        italic: data.italic,
        lineThrough: data.lineThrough,
        underline: data.underline,
        spans: (data.textContents ?? []).map((item) => createSpan(item, highlightResolver, enableHighlight)),
        lineHeightScale: data.lineSpacing,
        letterSpacing: data.letterSpacing,
        align: data.textAlign,
        writingMode: data.writingMode as TextRenderInput['writingMode'],
        width: fixedInlineSize && isHorizontal ? layer.width : null,
        height: fixedInlineSize && !isHorizontal ? layer.height : null,
    };

    const { textStroke = [], textShadow = [], textGlow = [], textBackground = [] } = data;
    if (textStroke.length > 0) input.strokes = textStroke;
    if (textShadow.length > 0) input.shadows = textShadow;
    if (textGlow.length > 0) input.glows = textGlow;
    if (textBackground.length > 0) input.backgrounds = textBackground;

    // 旧 DOM 排版遗留的单行文字框在校准前不参与软换行，详见 ./legacy-frame。
    if (shouldKeepLegacyFrameUnwrapped(layer)) input.noWrap = true;

    if (data.textGradient) input.gradient = data.textGradient;
    if (listMarker) input.listMarker = listMarker;
    if (highlight) input.highlight = highlight;
    if (enableTexture && data.texture && data.texture.url) input.texture = createTextureAsset(data.texture);
    if (
        enableArtText &&
        data.advancedTextMaterial &&
        data.advancedTextMaterial.material &&
        data.advancedTextMaterial.material.src
    ) {
        input.artText = {
            id: data.advancedTextMaterial.id,
            url: normalizeAssetURL(data.advancedTextMaterial.material.src),
            defaultFontSize: data.advancedTextMaterial.material.defaultFontSize,
            useCount: data.advancedTextMaterial.material.useCount,
        };
    }

    return input;
};
